import {
  useRef,
  useState,
  useLayoutEffect,
  type ReactNode,
  type MutableRefObject,
  type RefObject,
} from 'react';

import { useResizeObserver } from '@cognite/cogs-utils-v11/hooks/use-resize-observer/use-resize-observer.tsx';

import type {
  BreadcrumbsItemProps,
  BreadcrumbsHtmlElementProps,
} from './types';
import {
  getBreadcrumbsCollapsingOrder,
  getCollapsedItemsIndices,
} from './utils';

const DROPDOWN_TOGGLE_WIDTH = 36 as const;

export const useResponsiveBreadcrumbs = ({
  children,
  items,
  itemRefs,
  mainWrapperRef,
  isResponsive,
  isCollapsedAllAtOnce,
}: {
  children: ReactNode;
  items: BreadcrumbsItemProps[];
  itemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
  mainWrapperRef: RefObject<HTMLElement>;
  isResponsive?: boolean;
  isCollapsedAllAtOnce?: boolean;
}): {
  collapsedItems: number;
  visibleItemsLeftSide: number;
  shouldTruncateActiveItem: boolean | undefined;
} => {
  const prevChildren =
    useRef<BreadcrumbsHtmlElementProps<HTMLDivElement>['children']>(children);
  const [itemWidths, setItemWidths] = useState<number[]>([]);
  const itemsIndicesInCollapsingOrder: number[] = getBreadcrumbsCollapsingOrder(
    items.length
  );

  const mainWrapperSize = useResizeObserver(mainWrapperRef);

  // all items can be collapsed except the active item (last item)
  const maxCollapsableItems = Math.max(0, items.length - 1);
  // all items except the first and the last (active item)
  const totalMiddleItems = Math.max(0, items.length - 2);
  // number of items to collapse based on current width of container
  const [collapsedItems, setCollapsedItems] = useState(
    isCollapsedAllAtOnce ? totalMiddleItems : 0
  );
  // number of items to keep visible on the left side until all middle items
  // have been collapsed
  const [visibleItemsLeftSide, setVisibleItemsLeftSide] = useState(1);
  // if there is only one item or if all items except the active one are
  // collapsed, we should truncate the active item
  const shouldTruncateActiveItem =
    isResponsive && (items.length < 2 || collapsedItems >= maxCollapsableItems);

  // collapsing logic
  useLayoutEffect(() => {
    // if the children are changed, we reset the state to update it based on new
    // children's widths
    if (prevChildren.current !== children) {
      itemRefs.current = itemRefs.current.filter((el) => el !== null);
      setItemWidths([]);
      setCollapsedItems(isCollapsedAllAtOnce ? totalMiddleItems : 0);
      setVisibleItemsLeftSide(1);
      prevChildren.current = children;
      return;
    }

    // don't collapse if there is only 1 item
    // don't collapse if isResponsive is false
    // if `isCollapsedAllAtOnce` is `true`, we should collapse middle items
    // altogether instead of incrementally
    if (items.length < 2 || !isResponsive || isCollapsedAllAtOnce) return;

    if (itemWidths.length !== itemRefs.current.length) {
      setItemWidths(itemRefs.current.map((el) => el?.offsetWidth ?? 0));
      return;
    }

    const dropdownToggleThreshold =
      collapsedItems > 0 ? DROPDOWN_TOGGLE_WIDTH : 0;
    const collapsedIndices = getCollapsedItemsIndices({
      itemWidths,
      itemsIndicesInCollapsingOrder,
      mainWrapperWidth: mainWrapperSize.offsetWidth - dropdownToggleThreshold,
    });

    setCollapsedItems(Math.min(collapsedIndices.length, maxCollapsableItems));
    setVisibleItemsLeftSide(collapsedIndices.includes(0) ? 0 : 1);
  }, [
    children,
    collapsedItems,
    isCollapsedAllAtOnce,
    isResponsive,
    itemRefs,
    itemWidths,
    items.length,
    itemsIndicesInCollapsingOrder,
    mainWrapperSize.offsetWidth,
    maxCollapsableItems,
    totalMiddleItems,
    visibleItemsLeftSide,
  ]);

  return {
    collapsedItems,
    visibleItemsLeftSide,
    shouldTruncateActiveItem,
  };
};
